import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import 'moment/locale/th';
import shrinkImage from './shrinkImage';
import { setUrlParams } from './cleanerToken';

// หน้าเงินประกันของแม่บ้าน (/once/cleaner?view=deposit) -- docs/room-deposit-plan.md (API)
// เข้าพัก: ถ่ายสลิป -> ยืนยันรับเงิน (แจ้งกลุ่มอัตโนมัติ) · เช็คเอาท์: ตรวจห้อง ปกติ / เสียหาย (รูป + รายการ + โน้ต)
const MAX_PHOTOS = 5;
const money = (n) => Number(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 });
const checkoutOf = (b) => (b?.end_date ? moment(b.end_date).add(1, 'day') : null); // end_date = คืนสุดท้าย
const errOf = (e) => e?.response?.data?.message || 'ไม่สำเร็จ — สัญญาณเน็ตอาจไม่ดี ลองอีกครั้ง';

export default function DepositWork({ cw, S, onBack }) {
  const urlDate = new URLSearchParams(window.location.search).get('date');
  const [date, setDate] = useState(urlDate && moment(urlDate, 'YYYY-MM-DD', true).isValid() ? urlDate : moment().format('YYYY-MM-DD'));
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);     // { type: 'receive', arrival } | { type: 'inspect', dep }
  const [slip, setSlip] = useState(null);
  const [step, setStep] = useState(null);     // inspect: null | 'ok' | 'damaged'
  const [photos, setPhotos] = useState([]);
  const [qtys, setQtys] = useState({});       // { [expense_id]: qty }
  const [prices, setPrices] = useState({});   // { [expense_id]: ราคาที่แก้ (string) } -- ไม่มี = default_price
  const [customs, setCustoms] = useState([]); // รายการอื่นๆ (ไม่มีในรายการ) [{ key, name, qty, unit_price }]
  const [note, setNote] = useState('');
  const [options, setOptions] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);     // ข้อความสำเร็จ
  const [doneDoor, setDoneDoor] = useState(null); // ผลส่งรหัสประตูหลังรับเงิน { door, door_codes }

  const load = useCallback((d) => {
    setData(null);
    cw(`deposits?date=${d}`).then((r) => setData(r.data)).catch((e) => setData({ error: errOf(e), arrivals: [], checkouts: [] }));
  }, [cw]);

  useEffect(() => { moment.locale('th'); load(date); setUrlParams({ date }); }, [date, load]);
  useEffect(() => { cw('deposits/expense-options').then((r) => setOptions(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, [cw]);

  const shiftDate = (n) => setDate(moment(date).add(n, 'day').format('YYYY-MM-DD'));

  const upload = async (file, name) => {
    const small = await shrinkImage(file);
    const fd = new FormData(); fd.append('file', small, name);
    const r = await cw('upload-photo', { method: 'post', data: fd, timeout: 90000 });
    return r.data.url;
  };

  const onPickSlip = async (file) => {
    if (!file) return;
    setUploading(1);
    try { setSlip(await upload(file, 'deposit-slip.jpg')); } catch (e) { alert('ส่งรูปไม่สำเร็จ — ลองใหม่อีกครั้ง'); }
    setUploading(0);
  };

  const onPickPhotos = async (files) => {
    const list = Array.from(files || []).slice(0, MAX_PHOTOS - photos.length);
    if (!list.length) return;
    setUploading(list.length);
    for (const f of list) { // eslint-disable-line no-restricted-syntax
      try {
        const url = await upload(f, 'damage.jpg'); // eslint-disable-line no-await-in-loop
        setPhotos((p) => [...p, url].slice(0, MAX_PHOTOS));
      } catch (e) { alert('ส่งรูปไม่สำเร็จบางรูป — ลองใหม่อีกครั้ง'); }
      setUploading((n) => Math.max(0, n - 1));
    }
  };

  const openReceive = (arrival) => { setMode({ type: 'receive', arrival }); setSlip(null); setDone(null); setDoneDoor(null); };
  const openInspect = (dep) => { setMode({ type: 'inspect', dep }); setStep(null); setPhotos([]); setQtys({}); setPrices({}); setCustoms([]); setNote(''); setDone(null); };
  const closeMode = () => { setMode(null); setDone(null); setDoneDoor(null); load(date); };

  const submitReceive = () => {
    if (!slip) { alert('กรุณาแนบรูปสลิป/หลักฐานการรับเงินก่อน'); return; }
    setBusy(true);
    cw('deposits/receive', { method: 'post', data: { booking_id: mode.arrival.booking_id, slip_url: slip } })
      .then((r) => {
        setBusy(false);
        const door = r?.data?.door;
        // ห้อง digital door -> ผลส่งรหัสแสดงแยกด้านล่าง · ห้องปกติ -> เตือนมอบคีย์การ์ดเหมือนเดิม
        setDoneDoor(door?.door ? { door, door_codes: r.data.door_codes || [] } : null);
        setDone(door?.door ? 'บันทึกแล้ว ส่งแจ้งกลุ่มแล้ว' : 'บันทึกแล้ว ส่งแจ้งกลุ่มแล้ว · อย่าลืมมอบคีย์การ์ด/กุญแจ');
      })
      .catch((e) => { setBusy(false); alert(errOf(e)); });
  };

  // ราคาต่อชิ้นของรายการในแคตตาล็อก -- แก้ได้ ไม่แก้ = default_price
  const priceOf = (o) => (prices[o.id] !== undefined ? prices[o.id] : String(Number(o.default_price) || 0));
  const editCustom = (key, patch) => setCustoms((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  const addCustom = () => setCustoms((cs) => [...cs, { key: Date.now(), name: '', qty: 1, unit_price: '' }]);
  const damageTotal = options.reduce((s, o) => s + (qtys[o.id] || 0) * (Number(priceOf(o)) || 0), 0)
    + customs.reduce((s, c) => s + (Number(c.qty) || 0) * (Number(c.unit_price) || 0), 0);

  const submitInspect = (result) => {
    const items = [
      ...options.filter((o) => (qtys[o.id] || 0) > 0).map((o) => ({ expense_id: o.id, qty: qtys[o.id], unit_price: Number(priceOf(o)) || 0 })),
      ...customs.filter((c) => Number(c.qty) > 0).map((c) => ({ name: c.name.trim(), qty: Number(c.qty), unit_price: Number(c.unit_price) })),
    ];
    if (result === 'damaged') {
      if (uploading) { alert('รอรูปส่งเสร็จก่อนนะคะ'); return; }
      if (customs.some((c) => Number(c.qty) > 0 && !c.name.trim())) { alert('รายการอื่นๆ: กรุณาใส่ชื่อของที่เสียหาย'); return; }
      if (customs.some((c) => Number(c.qty) > 0 && !(Number(c.unit_price) > 0))) { alert('รายการอื่นๆ: กรุณาใส่ยอดที่หัก (มากกว่า 0 บาท)'); return; }
      if (options.some((o) => (qtys[o.id] || 0) > 0 && !(Number(priceOf(o)) >= 0))) { alert('ราคาไม่ถูกต้อง'); return; }
      if (!note.trim() && !photos.length && !items.length) { alert('ใส่รูป หรือเลือกรายการ หรือเขียนโน้ต อย่างน้อย 1 อย่าง'); return; }
    }
    setBusy(true);
    cw(`deposits/${mode.dep.id}/inspect`, { method: 'post', data: result === 'ok' ? { result } : { result, note: note.trim(), photos, items } })
      .then(() => { setBusy(false); setDone(result === 'ok' ? 'บันทึกแล้ว · ห้องปกติ' : 'บันทึกแล้ว · แจ้งผู้ดูแลเงินแล้ว'); })
      .catch((e) => { setBusy(false); alert(errOf(e)); });
  };

  const input = { boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 12, fontSize: 16 };
  const codeBox = { fontFamily: 'monospace', fontSize: 22, fontWeight: 800, letterSpacing: 1 };
  const visibleCodes = (list) => (list || []).filter((c) => c.code);
  // รหัสประตูในการ์ดเข้าพัก -- code = null แปลว่ายังไม่ถึงเวลาให้แม่บ้านเห็น (รอรับเงินประกันก่อน)
  // WiFi ประจำห้อง -- แม่บ้านเห็นเสมอ (บอกลูกค้าได้ถ้าไม่ได้ SMS)
  const wifiLine = (c) => c.wifi && (
    <div style={{ fontSize: 14, color: '#2C6DAF', fontWeight: 700, marginTop: 4 }}>
      📶 WiFi {c.room_name}: {c.wifi.ssid} · {c.wifi.password ? `รหัส ${c.wifi.password}` : 'ไม่มีรหัส'}
    </div>
  );
  // door = false -> ห้องมีแต่ WiFi ไม่ใช่ digital door
  const doorLines = (list) => (list || []).map((c) => (
    <div key={c.room_name} style={{ marginTop: 8 }}>
      {c.door !== false && (c.code
        ? <div><span style={codeBox}>🔑 รหัส {c.room_name}: {c.code}</span>{c.sent && <div style={{ fontSize: 13, color: '#0F8A56', fontWeight: 700 }}>ส่ง SMS แล้ว</div>}</div>
        : c.has_code
          ? <div style={{ fontSize: 14, color: '#586159', fontWeight: 700 }}>🔑 {c.room_name}: รหัสประตูจะส่งให้ลูกค้าหลังรับเงินประกัน</div>
          : <div style={{ fontSize: 14, color: '#CD3E33', fontWeight: 700 }}>🔑 {c.room_name}: ยังไม่มีรหัสประตู — แจ้งแอดมิน / ใช้คีย์การ์ด</div>)}
      {wifiLine(c)}
    </div>
  ));
  const cancelBtn = <button style={{ ...S.back, width: '100%', marginTop: 8, color: '#586159' }} onClick={closeMode}>ยกเลิก</button>;

  // ---------- สำเร็จ ----------
  if (done) {
    return (
      <div style={S.page}>
        <div style={{ ...S.card, background: '#F1F7F3', borderColor: '#0F8A56', textAlign: 'center', marginTop: 20 }}>
          <div style={{ fontSize: 44 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 8 }}>{done}</div>
        </div>
        {doneDoor && (() => {
          const { door } = doneDoor;
          const codes = visibleCodes(doneDoor.door_codes);
          return (
            <div style={{ ...S.card, borderColor: door.missing?.length || door.noPhone ? '#B26B0E' : '#0F8A56' }}>
              {door.sent && <div style={{ fontSize: 16, fontWeight: 800, color: '#0F8A56' }}>
                📱 ส่ง{[
                  (doneDoor.door_codes || []).some((c) => c.door !== false) && 'รหัสประตู',
                  (doneDoor.door_codes || []).some((c) => c.wifi) && 'WiFi',
                ].filter(Boolean).join(' + ') || 'รหัสประตู'} ให้ลูกค้าทาง SMS แล้ว
              </div>}
              {door.noPhone && <div style={{ fontSize: 16, fontWeight: 800, color: '#B26B0E' }}>📵 ลูกค้าไม่มีเบอร์ — บอกรหัสให้ลูกค้า</div>}
              {door.missing?.length > 0 && (
                <div style={{ fontSize: 16, fontWeight: 800, color: '#CD3E33', marginTop: door.sent || door.noPhone ? 8 : 0 }}>
                  ⚠️ ยังไม่มีรหัสประตูห้อง {door.missing.join(', ')} — แจ้งแอดมินแล้ว · มอบคีย์การ์ดไปก่อน
                </div>
              )}
              {(door.sent || door.noPhone) && codes.map((c) => (
                <div key={c.room_name} style={{ ...codeBox, marginTop: 8 }}>🔑 รหัส {c.room_name}: {c.code}</div>
              ))}
              {(door.sent || door.noPhone) && (doneDoor.door_codes || []).map((c) => <div key={`w-${c.room_name}`}>{wifiLine(c)}</div>)}
            </div>
          );
        })()}
        <button style={S.bigBtn('#0F8A56')} onClick={closeMode}>เรียบร้อย</button>
      </div>
    );
  }

  // ---------- รับเงินประกัน ----------
  if (mode?.type === 'receive') {
    const a = mode.arrival;
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={closeMode}>‹ กลับ</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>รับเงินประกัน ฿{money(a.outstanding)}</h1><div style={S.sub}>{a.room_name} · {a.contact_name}</div></div>
        </div>
        <div style={S.card}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>1. ถ่ายรูปสลิปโอน / เงินสดที่รับ</div>
          {slip
            ? <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={slip} alt="สลิป" style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 10 }} />
                <button onClick={() => setSlip(null)} style={{ position: 'absolute', top: 6, right: 6, width: 34, height: 34, borderRadius: 17, border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 16 }}>✕</button>
              </div>
            : <label style={{ display: 'block', border: '2px dashed #CBD4CC', borderRadius: 12, padding: 24, fontSize: 18, fontWeight: 800, textAlign: 'center' }}>
                {uploading ? '⏳ กำลังส่งรูป…' : '📷 ถ่าย / แนบรูปสลิป'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { onPickSlip(e.target.files?.[0]); e.target.value = ''; }} />
              </label>}
        </div>
        <button disabled={busy || !!uploading || !slip} style={S.bigBtn(slip ? '#0F8A56' : '#8A938B')} onClick={submitReceive}>
          {busy ? 'กำลังส่ง…' : `2. ยืนยันรับเงิน ฿${money(a.outstanding)}`}
        </button>
        {cancelBtn}
      </div>
    );
  }

  // ---------- ตรวจห้อง ----------
  if (mode?.type === 'inspect') {
    const d = mode.dep;
    const co = checkoutOf(d.booking);
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={closeMode}>‹ กลับ</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>ตรวจห้อง · {d.room_name}</h1><div style={S.sub}>{d.booking?.contact_name}{co ? ` · ออก ${co.format('D MMM')}` : ''}</div></div>
        </div>

        {!step && (
          <>
            <button style={{ ...S.bigBtn('#0F8A56'), padding: 26, fontSize: 22 }} onClick={() => setStep('ok')}>✅ ห้องปกติ</button>
            <button style={{ ...S.bigBtn('#B26B0E'), padding: 26, fontSize: 22 }} onClick={() => setStep('damaged')}>⚠️ มีความเสียหาย</button>
            {cancelBtn}
          </>
        )}

        {step === 'ok' && (
          <div style={{ ...S.card, textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>ยืนยัน ห้องปกติ ไม่มีความเสียหาย?</div>
            <div style={{ ...S.sub, marginTop: 4 }}>ผู้ดูแลเงินจะคืนเงินประกันเต็มจำนวนให้ลูกค้า</div>
            <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={() => submitInspect('ok')}>{busy ? 'กำลังส่ง…' : 'ยืนยัน ห้องปกติ'}</button>
            <button style={{ ...S.back, width: '100%', marginTop: 8, color: '#586159' }} onClick={() => setStep(null)}>ย้อนกลับ</button>
          </div>
        )}

        {step === 'damaged' && (
          <>
            <div style={S.card}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>📷 รูปความเสียหาย ({photos.length}/{MAX_PHOTOS})</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {photos.map((u) => (
                  <div key={u} style={{ position: 'relative' }}>
                    <img src={u} alt="" style={{ display: 'block', width: '100%', height: 92, objectFit: 'cover', borderRadius: 10 }} />
                    <button onClick={() => setPhotos((p) => p.filter((x) => x !== u))}
                      style={{ position: 'absolute', top: 4, right: 4, width: 32, height: 32, borderRadius: 16, border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 16, fontWeight: 800, lineHeight: '32px', padding: 0 }}>✕</button>
                  </div>
                ))}
                {photos.length + uploading < MAX_PHOTOS && (
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 92, border: '2px dashed #CBD4CC', borderRadius: 10, fontSize: 28 }}>
                    +
                    <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { onPickPhotos(e.target.files); e.target.value = ''; }} />
                  </label>
                )}
                {uploading > 0 && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 92, fontSize: 13, color: '#586159' }}>⏳ กำลังส่ง {uploading} รูป</div>}
              </div>
            </div>

            <div style={S.card}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>ของที่เสียหาย (ถ้ามี)</div>
              {options.map((o) => {
                const q = qtys[o.id] || 0;
                const setQ = (n) => setQtys((m) => ({ ...m, [o.id]: Math.max(0, n) }));
                const p = priceOf(o);
                return (
                  <div key={o.id} style={{ padding: '8px 0', borderTop: '1px solid #EEF1EE' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, fontSize: 15, fontWeight: q ? 800 : 600 }}>{o.name}<div style={{ fontSize: 12, color: '#8A938B' }}>฿{money(o.default_price)}</div></div>
                      <button style={{ ...S.back, fontSize: 20, padding: '4px 14px' }} onClick={() => setQ(q - 1)}>−</button>
                      <div style={{ minWidth: 24, textAlign: 'center', fontSize: 18, fontWeight: 800 }}>{q}</div>
                      <button style={{ ...S.back, fontSize: 20, padding: '4px 14px' }} onClick={() => setQ(q + 1)}>+</button>
                    </div>
                    {q > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        <span style={{ fontSize: 14, color: '#586159' }}>ราคา/ชิ้น ฿</span>
                        <input type="number" inputMode="decimal" min="0" value={p} onChange={(e) => setPrices((m) => ({ ...m, [o.id]: e.target.value }))}
                          style={{ ...input, width: 0, flex: 1, minWidth: 80, padding: 10 }} />
                        <b style={{ minWidth: 70, textAlign: 'right' }}>฿{money(q * (Number(p) || 0))}</b>
                      </div>
                    )}
                  </div>
                );
              })}

              {customs.map((c) => (
                <div key={c.key} style={{ padding: '8px 0', borderTop: '1px solid #EEF1EE' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input value={c.name} onChange={(e) => editCustom(c.key, { name: e.target.value })} placeholder="ชื่อของที่เสียหาย เช่น ผ้าม่านขาด"
                      style={{ ...input, width: 0, flex: 1, padding: 10 }} />
                    <button onClick={() => setCustoms((cs) => cs.filter((x) => x.key !== c.key))}
                      style={{ ...S.back, color: '#CD3E33', fontSize: 18, padding: '6px 14px' }}>×</button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <button style={{ ...S.back, fontSize: 20, padding: '4px 14px' }} onClick={() => editCustom(c.key, { qty: Math.max(1, Number(c.qty) - 1) })}>−</button>
                    <div style={{ minWidth: 24, textAlign: 'center', fontSize: 18, fontWeight: 800 }}>{c.qty}</div>
                    <button style={{ ...S.back, fontSize: 20, padding: '4px 14px' }} onClick={() => editCustom(c.key, { qty: Number(c.qty) + 1 })}>+</button>
                    <span style={{ fontSize: 14, color: '#586159' }}>ยอดที่หัก ฿</span>
                    <input type="number" inputMode="decimal" min="0" value={c.unit_price} onChange={(e) => editCustom(c.key, { unit_price: e.target.value })} placeholder="0"
                      style={{ ...input, width: 0, flex: 1, minWidth: 70, padding: 10 }} />
                  </div>
                  {Number(c.qty) > 1 && Number(c.unit_price) > 0 && (
                    <div style={{ fontSize: 13, color: '#586159', textAlign: 'right', marginTop: 4 }}>{c.qty} × ฿{money(c.unit_price)} = <b>฿{money(c.qty * Number(c.unit_price))}</b></div>
                  )}
                </div>
              ))}

              <button style={{ ...S.back, width: '100%', marginTop: 8, padding: 12, fontSize: 16, color: '#2C6DAF' }} onClick={addCustom}>+ อื่นๆ (ไม่มีในรายการ)</button>

              {damageTotal > 0 && (
                <div style={{ display: 'flex', alignItems: 'baseline', marginTop: 10, paddingTop: 10, borderTop: '1px solid #EEF1EE' }}>
                  <div style={{ flex: 1, fontWeight: 800 }}>หักรวม</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#B26B0E' }}>{money(damageTotal)} บาท</div>
                </div>
              )}
            </div>

            <div style={S.card}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>โน้ต</div>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="เช่น ผ้าปูมีคราบไวน์ แก้วแตก 1 ใบ" style={input} />
            </div>

            <button disabled={busy || !!uploading} style={S.bigBtn(uploading ? '#8A938B' : '#B26B0E')} onClick={() => submitInspect('damaged')}>
              {busy ? 'กำลังส่ง…' : uploading ? '⏳ รอรูปส่งเสร็จ…' : 'ส่งรายงานความเสียหาย'}
            </button>
            <button style={{ ...S.back, width: '100%', marginTop: 8, color: '#586159' }} onClick={() => setStep(null)}>ย้อนกลับ</button>
          </>
        )}
      </div>
    );
  }

  // ---------- LIST ----------
  const arrivals = data?.arrivals || [];
  const checkouts = data?.checkouts || [];
  const isToday = date === moment().format('YYYY-MM-DD');
  return (
    <div style={S.page}>
      <div style={S.bar}>
        <button style={S.back} onClick={onBack}>‹ หน้าหลัก</button>
        <div><h1 style={{ ...S.h, fontSize: 17 }}>เงินประกัน · รหัสประตู</h1><div style={S.sub}>รับเงินตอนเข้าพัก · ตรวจห้องหลังเช็คเอาท์</div></div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <button style={{ ...S.back, fontSize: 18, padding: '8px 14px' }} onClick={() => shiftDate(-1)}>‹</button>
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)}
          style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 10, fontSize: 16, fontWeight: 700, textAlign: 'center', background: '#fff' }} />
        <button style={{ ...S.back, fontSize: 18, padding: '8px 14px' }} onClick={() => shiftDate(1)}>›</button>
      </div>
      {!isToday && <button style={{ ...S.back, width: '100%', marginBottom: 12, color: '#2C6DAF' }} onClick={() => setDate(moment().format('YYYY-MM-DD'))}>กลับไปวันนี้</button>}

      {data === null && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>กำลังโหลด…</div>}
      {data?.error && <div style={{ ...S.card, textAlign: 'center', color: '#CD3E33' }}>{data.error}</div>}

      {data && !data.error && (
        <>
          <div style={{ fontWeight: 800, margin: '4px 0 8px' }}>🔑 เข้าพัก{isToday ? 'วันนี้' : ` ${moment(date).format('D MMM')}`} · รับเงินประกัน / รหัสประตู</div>
          {arrivals.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ไม่มีแขกที่ต้องรับเงินประกัน / รหัสประตู</div>}
          {arrivals.map((a) => {
            const st = a.deposit?.state;
            return (
              <div key={a.booking_id} style={{ ...S.card, borderColor: !a.deposit && a.outstanding > 0 ? '#B26B0E' : '#DBE2DC' }}>
                <div style={{ fontSize: 17, fontWeight: 800 }}>{a.room_name}</div>
                <div style={S.sub}>{a.contact_name} · #{a.booking_no}</div>
                {!a.deposit && a.outstanding > 0 && (
                  <button style={{ ...S.bigBtn('#B26B0E'), padding: 14, fontSize: 18 }} onClick={() => openReceive(a)}>💵 รับเงินประกัน {money(a.outstanding)} บาท</button>
                )}
                {st === 'customer_paid' && <div style={{ ...S.pill('#0F8A56'), marginTop: 8 }}>รับแล้ว ✓ ฿{money(a.deposit.deposit_amount)}</div>}
                {st === 'waiting_approve' && <div style={{ ...S.pill('#2C6DAF'), marginTop: 8 }}>จ่ายออนไลน์แล้ว รอตรวจ</div>}
                {st === 'refunded' && <div style={{ ...S.pill('#8A938B'), marginTop: 8 }}>คืนเงินแล้ว</div>}
                {!!a.waived && !a.deposit && (
                  <div style={{ marginTop: 8 }}>
                    <div style={S.pill('#2C6DAF')}>ยกเว้นเงินประกัน · {a.waived.reason_label} — ไม่ต้องเก็บ มอบกุญแจได้เลย</div>
                    {!!a.waived.note && <div style={{ ...S.sub, marginTop: 4 }}>{a.waived.note}</div>}
                  </div>
                )}
                {doorLines(a.door_codes)}
              </div>
            );
          })}

          <div style={{ fontWeight: 800, margin: '18px 0 8px' }}>🧳 เช็คเอาท์ · ตรวจห้อง</div>
          {checkouts.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ไม่มีห้องที่รอตรวจ</div>}
          {checkouts.map((d) => {
            const co = checkoutOf(d.booking);
            return (
              <div key={d.id} style={{ ...S.card, borderColor: '#2C6DAF' }}>
                <div style={{ fontSize: 17, fontWeight: 800 }}>{d.room_name}</div>
                <div style={S.sub}>{d.booking?.contact_name} · #{d.booking?.booking_no}{co ? ` · ออก ${co.format('D MMM')}` : ''}</div>
                <div style={{ fontSize: 13, color: '#586159', marginTop: 2 }}>เงินประกัน ฿{money(d.deposit_amount)}</div>
                <button style={{ ...S.bigBtn('#2C6DAF'), padding: 14, fontSize: 18 }} onClick={() => openInspect(d)}>🔍 ตรวจห้อง</button>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
